import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Copy, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { loginUser, clearError, sessionEstablished } from '@/store/auth/authSlice';
import { establishOrgScope } from '@/store/auth/orgSessionSlice';
import {
  clearMfaError,
  confirmMfaEnrollmentStep,
  mfaRecoveryAcknowledged,
  mfaReset,
  performMfaEnrollment,
  verifyMfaCode,
} from '@/store/auth/mfaSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

/** 6-digit authenticator code (RFC 6238 TOTP as served by auth-service). */
function OtpCodeField({
  value,
  onChange,
  disabled,
  invalid,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  invalid: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  // Move focus to the code field when it appears (keyboard/AT users).
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div className="space-y-2">
      <Label htmlFor="mfa-code">Verification code</Label>
      <Input
        ref={ref}
        id="mfa-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]{6}"
        maxLength={6}
        placeholder="123456"
        aria-describedby="mfa-code-help"
        aria-invalid={invalid}
        className="text-center tracking-widest"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        disabled={disabled}
        required
      />
      <p id="mfa-code-help" className="text-xs text-muted-foreground">
        Enter the 6-digit code from your authenticator app.
      </p>
    </div>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context / permissions): the full text
      // remains visible and selectable for manual copying.
    }
  };
  return (
    <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
      {copied ? (
        <Check className="mr-2 h-4 w-4" aria-hidden />
      ) : (
        <Copy className="mr-2 h-4 w-4" aria-hidden />
      )}
      {copied ? 'Copied' : label}
    </Button>
  );
}

export function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useAppSelector((s) => s.auth);
  const mfa = useAppSelector((s) => s.mfa);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(loginUser({ email, password }));
    if (loginUser.fulfilled.match(result) && result.payload.kind === 'ok') {
      // The login token carries no org scope; every org-scoped service 403s
      // without it. Exchange it for an org-scoped pair before entering the app.
      await dispatch(establishOrgScope());
      navigate('/dashboard', { replace: true });
    }
    // kind === 'mfa': the step below replaces the password form; no navigation.
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return;
    if (mfa.step === 'verify') {
      const result = await dispatch(verifyMfaCode(code));
      if (verifyMfaCode.fulfilled.match(result)) {
        dispatch(sessionEstablished({ user: result.payload.user, token: result.payload.token }));
        dispatch(mfaReset());
        await dispatch(establishOrgScope());
        navigate('/dashboard', { replace: true });
      }
    } else if (mfa.step === 'setup') {
      // Recovery codes arrive ONCE at enroll; capture them from this call's
      // payload (the selector closure is stale until the next render).
      let codes = mfa.recoveryCodes;
      if (!mfa.otpauthUri) {
        const enrolled = await dispatch(performMfaEnrollment());
        if (!performMfaEnrollment.fulfilled.match(enrolled)) return;
        codes = enrolled.payload.recoveryCodes;
      }
      // Two-sequential-code confirmation: the first accepted code returns
      // { confirmed:false, nextPreAuthJwt } (no session). We stay on the
      // form and ask for the NEXT authenticator code.
      const result = await dispatch(confirmMfaEnrollmentStep(code));
      if (confirmMfaEnrollmentStep.fulfilled.match(result)) {
        if (result.payload.kind === 'pending') {
          // Keep the setup step; the slice refreshed the single-use ticket.
          setCode('');
          return;
        }
        dispatch(sessionEstablished({ user: result.payload.user, token: result.payload.token }));
        // If recovery codes must be shown once, stay on the recovery screen
        // (the slice set step='recovery'); else enter the app immediately.
        if (codes.length === 0) {
          dispatch(mfaReset());
          await dispatch(establishOrgScope());
          navigate('/dashboard', { replace: true });
        }
      }
    }
    setCode('');
  };

  const handleSetupStart = () => {
    dispatch(clearMfaError());
    dispatch(performMfaEnrollment());
  };

  const handleRecoveryDone = () => {
    dispatch(mfaRecoveryAcknowledged());
    navigate('/dashboard', { replace: true });
  };

  const handleCancelMfa = () => {
    dispatch(mfaReset());
    dispatch(clearError());
    setCode('');
  };

  const mfaError = mfa.error;
  const mfaBusy = mfa.isLoading;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold text-xl">
            C
          </div>
          <CardTitle className="text-2xl">Cluster Admin</CardTitle>
          <CardDescription>Sign in to manage your cluster</CardDescription>
        </CardHeader>
        <CardContent>
          {mfa.step === 'recovery' ? (
            /* ── One-time recovery codes display ─────────────────────── */
            <div className="space-y-4">
              <div role="status" className="flex items-start gap-3 rounded-md bg-muted p-3 text-sm">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                <div>
                  <p className="font-medium">Save your recovery codes now</p>
                  <p className="text-muted-foreground">
                    These codes are shown only once. Each works one time if you lose access to your
                    authenticator app.
                  </p>
                </div>
              </div>
              <ul
                aria-label="Recovery codes"
                className="grid grid-cols-2 gap-2 rounded-md border border-dashed p-3 font-mono text-sm"
              >
                {mfa.recoveryCodes.map((rc) => (
                  <li key={rc}>{rc}</li>
                ))}
              </ul>
              <div className="flex items-center justify-between gap-2">
                <CopyButton text={mfa.recoveryCodes.join('\n')} label="Copy codes" />
                <Button type="button" onClick={handleRecoveryDone}>
                  I saved my codes
                </Button>
              </div>
            </div>
          ) : mfa.step === 'verify' || mfa.step === 'setup' ? (
            /* ── Second-factor step (verify existing MFA / enroll new) ── */
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              {mfaError && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {mfaError}
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                {mfa.step === 'verify'
                  ? 'This account requires a second factor. Enter the code from your authenticator app.'
                  : mfa.firstCodeAccepted
                    ? 'First code confirmed. Wait for a NEW code from your authenticator app and enter it to finish setup.'
                    : 'Finish setting up two-factor authentication for your account. Enter the current code from your authenticator app.'}
              </p>

              {mfa.step === 'setup' && !mfa.otpauthUri && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={mfaBusy}
                  onClick={handleSetupStart}
                >
                  {mfaBusy ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                      Preparing setup...
                    </>
                  ) : (
                    'Start MFA setup'
                  )}
                </Button>
              )}

              {mfa.step === 'setup' && mfa.otpauthUri && (
                <div className="space-y-2">
                  <Label id="otp-setup-label">Set up your authenticator app</Label>
                  <p className="text-xs text-muted-foreground">
                    Add this key to your authenticator app, then enter the current code below to
                    finish. This value is never stored on this device.
                  </p>
                  <p
                    aria-labelledby="otp-setup-label"
                    className="break-all rounded-md bg-muted p-3 font-mono text-xs"
                  >
                    {mfa.otpauthUri}
                  </p>
                  <CopyButton text={mfa.otpauthUri} label="Copy setup key" />
                </div>
              )}

              {(mfa.step === 'verify' || (mfa.step === 'setup' && mfa.otpauthUri)) && (
                <>
                  <OtpCodeField
                    value={code}
                    onChange={(next) => {
                      setCode(next);
                      dispatch(clearMfaError());
                    }}
                    disabled={mfaBusy}
                    invalid={!!mfaError}
                  />
                  <Button type="submit" className="w-full" disabled={mfaBusy || code.length !== 6}>
                    {mfaBusy ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                        Verifying...
                      </>
                    ) : (
                      'Verify code'
                    )}
                  </Button>
                </>
              )}

              <Button type="button" variant="ghost" className="w-full" onClick={handleCancelMfa}>
                Use a different account
              </Button>
            </form>
          ) : (
            /* ── Credentials step ──────────────────────────────────────── */
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@cluster.io"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    dispatch(clearError());
                  }}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    dispatch(clearError());
                  }}
                  required
                  autoComplete="current-password"
                  minLength={12}
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
